import { ColDef, GridReadyEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle } from 'src/utils/grid/gridStyleUtils';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

import { PRD5010_R01Dto, defaultPRD5010_R01Data } from './Dto_PRD5010_R01';
import { usePRD5010_R01Store } from './Store_PRD5010_R01';

const COLUMN_STATE_KEY = 'PRD5010_R01-grid-column-state';

export const Table_PRD5010_R01 = () => {
	const gridRef = useRef<AgGridReact<PRD5010_R01Dto>>(null);

	const {
		PRD5010_R01List,
		selectedPRD5010_R01List,
		columnState,
		setColumnState,
		scrollTop,
		setScrollTop,
	} = usePRD5010_R01Store();

	const { compCdList } = useCommonCodeStore();
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const codeMaps = useMemo(() => ({
		comp: new Map(compCdList.map(item => [item.compCd, item.compNm])),
	}), [compCdList]);

	const {
		getRowId,
		settingRowIndex,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: PRD5010_R01List || [],
		defaultGridData: defaultPRD5010_R01Data,
		selectedRow: selectedPRD5010_R01List,
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		compCd: '사업장',
		compNm: '사업장명',
		ocrdttm: '전송일자',
		systmopryn: '시스템여부',
		kpifldcd: 'KPI코드',
		kpidtlnm: '상세코드명',
		kpidtlcd: '상세코드',
		msmtvl: '실적값',
		unt: '데이터명',
		achrt: '성장율',
		remark: '비고',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'compCd')(params), []);

	const baseColumnDefs: ColDef<PRD5010_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '사업장', colId: 'compCd', field: 'compCd', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'compCd'),
		},
		{
			headerName: '사업장명', colId: 'compNm', field: 'compNm', width: 150,
			headerClass: aggridHeaderClass,
			valueGetter: (params) => codeMaps.comp.get(params.data?.compCd ?? '') || params.data?.compNm || '',
			cellStyle: (p) => getCellStyle(p, 'compNm'),
		},
		{
			headerName: '전송일자', colId: 'ocrdttm', field: 'ocrdttm', width: 120,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'ocrdttm'),
		},
		{
			headerName: '시스템여부', colId: 'systmopryn', field: 'systmopryn', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'systmopryn'),
		},
		{
			headerName: 'KPI코드', colId: 'kpifldcd', field: 'kpifldcd', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'kpifldcd'),
		},
		{
			headerName: '상세코드명', colId: 'kpidtlnm', field: 'kpidtlnm', width: 150,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'kpidtlnm'),
		},
		{
			headerName: '상세코드', colId: 'kpidtlcd', field: 'kpidtlcd', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'kpidtlcd'),
		},
		{
			headerName: '실적값', colId: 'msmtvl', field: 'msmtvl', width: 100,
			type: 'numericColumn',
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'msmtvl'),
		},
		{
			headerName: '데이터명', colId: 'unt', field: 'unt', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'unt'),
		},
		{
			headerName: '성장율', colId: 'achrt', field: 'achrt', width: 100,
			type: 'numericColumn',
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'achrt'),
		},
		{
			headerName: '비고', colId: 'remark', field: 'remark', flex: 1,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'remark'),
		},
	], [getCellStyle, codeMaps.comp]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<PRD5010_R01Dto>[] = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<PRD5010_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		compCd: `${PRD5010_R01List.length.toLocaleString()}건`,
		editable: false,
	}]), [PRD5010_R01List]);

	return (
		<div className="flex flex-col gap-3 h-full min-h-0">
			<GridContextWrapper<PRD5010_R01Dto>
				enableCellCalculation
				gridRef={gridRef}
				paddedGridData={PRD5010_R01List}
				initSelected={() => {}}
				defaultRowData={defaultPRD5010_R01Data}
				baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
				defaultColumnState={defaultColumnState}
				setColumnState={setColumnState}
				localStorageKey={COLUMN_STATE_KEY}
				menuItems={undefined}
			>
				<Grid_Touch<PRD5010_R01Dto>
					ref={gridRef}
					rowData={PRD5010_R01List}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					getRowId={getRowId}
					setHoveredRowIndex={setHoveredRowIndex}
					onCellKeyDown={undefined}
					columnStateKey={COLUMN_STATE_KEY}
					setColumnState={setColumnState}
					scrollTop={scrollTop}
					setScrollTop={setScrollTop}
					pinnedBottomRowData={pinnedBottomRow}
					onGridReady={(_params: GridReadyEvent) => {}}
					suppressRowClickSelection={true}
					animateRows={true}
					enableCellTextSelection={true}
					ensureDomOrder={false}
					getRowStyle={getRowStyle}
				/>
			</GridContextWrapper>
		</div>
	);
};
